/**
 * Knowledge Module — Hỏi đáp tài liệu (Phase D 2026-05-18)
 *
 * Stack: Markitdown + bge-m3 + Qdrant + Claude Max OAuth.
 * Backend: /api/v1/knowledge/*
 *
 * Layout (Claude.ai-style):
 *   ┌──────────┬──────────────────────────────────┐
 *   │ Sidebar  │  Header (collection name + actions) │
 *   │ Collections│ ────────────────────────────────│
 *   │          │  Tabs: [Documents] [Chat Q&A]    │
 *   │ + New    │  ────────────────────────────────│
 *   │          │  Main content                    │
 *   │  - col1  │                                  │
 *   │  - col2  │                                  │
 *   └──────────┴──────────────────────────────────┘
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    Box, Typography, TextField, IconButton, List, ListItemButton, ListItemText,
    Drawer, Chip, CircularProgress, Alert, Button, Divider, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Tooltip,
    Paper, Accordion, AccordionSummary, AccordionDetails,
    Select, MenuItem, FormControl, InputLabel, FormHelperText
} from '@mui/material';
import {
    IconBook2, IconUpload, IconMessageCircle, IconFile, IconCircleCheck,
    IconAlertCircle, IconLoader2, IconRefresh, IconSend, IconPlus, IconTrash,
    IconFileTypePdf, IconFileTypeDocx, IconFileTypeXls, IconPhoto, IconChevronDown,
    IconLayoutSidebar, IconQuote
} from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || '/api/v1' });
api.interceptors.request.use((cfg) => {
    const token = localStorage.getItem('serviceToken') || localStorage.getItem('auth_token');
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
});

const getAuthHeader = () => {
    const t = localStorage.getItem('serviceToken') || localStorage.getItem('auth_token');
    return t ? `Bearer ${t}` : '';
};

const MAX_WIDTH = 820;

const STATUS_COLOR = {
    pending: 'default',
    converting: 'info',
    chunking: 'info',
    embedding: 'info',
    ready: 'success',
    failed: 'error'
};

const STATUS_LABEL = {
    pending: 'Chờ',
    converting: 'Convert',
    chunking: 'Chunk',
    embedding: 'Embed',
    ready: 'Sẵn sàng',
    failed: 'Lỗi'
};

const fileIcon = (filename) => {
    const ext = (filename || '').split('.').pop().toLowerCase();
    if (ext === 'pdf') return <IconFileTypePdf size={18} color="#d32f2f" />;
    if (['docx', 'doc'].includes(ext)) return <IconFileTypeDocx size={18} color="#1976d2" />;
    if (['xlsx', 'xls', 'csv'].includes(ext)) return <IconFileTypeXls size={18} color="#388e3c" />;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <IconPhoto size={18} color="#7b1fa2" />;
    return <IconFile size={18} color="#616161" />;
};

const fmtSize = (n) => {
    if (!n) return '0';
    if (n < 1024) return `${n}B`;
    if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)}K`;
    if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)}M`;
    return `${(n / 1024 ** 3).toFixed(2)}G`;
};

// ──────────────────────────────────────────────────────
// Markdown renderer (giống Agent Hub)
// ──────────────────────────────────────────────────────
// Shared word-wrap rules (port từ AgentHubPage)
const WRAP_SX = { wordBreak: 'break-word', overflowWrap: 'anywhere' };
const DEFAULT_VISIBLE = 5;
const VISIBLE_INCREMENT = 5;

const Md = ({ children }) => (
    <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
            code({ inline, className, children, ...props }) {
                const m = /language-(\w+)/.exec(className || '');
                return !inline && m ? (
                    <Box sx={{ ...WRAP_SX, overflowX: 'auto', maxWidth: '100%' }}>
                        <SyntaxHighlighter style={vscDarkPlus} language={m[1]} PreTag="div"
                            wrapLongLines
                            customStyle={{ borderRadius: 8, fontSize: 13, margin: '12px 0', maxWidth: '100%' }}
                            codeTagProps={{ style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }}
                            {...props}>
                            {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                    </Box>
                ) : (
                    <code style={{ background: '#f1f3f5', padding: '2px 6px', borderRadius: 4, fontSize: '0.92em', fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace', wordBreak: 'break-all' }} className={className} {...props}>{children}</code>
                );
            },
            p: ({ children }) => <Typography component="p" sx={{ my: 1, lineHeight: 1.7, fontSize: 14.5, ...WRAP_SX }}>{children}</Typography>,
            h1: ({ children }) => <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600, ...WRAP_SX }}>{children}</Typography>,
            h2: ({ children }) => <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600, ...WRAP_SX }}>{children}</Typography>,
            h3: ({ children }) => <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 600, ...WRAP_SX }}>{children}</Typography>,
            ul: ({ children }) => <Box component="ul" sx={{ pl: 3, my: 1, ...WRAP_SX }}>{children}</Box>,
            ol: ({ children }) => <Box component="ol" sx={{ pl: 3, my: 1, ...WRAP_SX }}>{children}</Box>,
            li: ({ children }) => <Box component="li" sx={{ my: 0.3, lineHeight: 1.7, ...WRAP_SX }}>{children}</Box>,
            a: ({ children, href }) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>{children}</a>,
            table: ({ children }) => <Box sx={{ overflowX: 'auto', my: 1.5, maxWidth: '100%' }}><table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>{children}</table></Box>,
            th: ({ children }) => <th style={{ border: '1px solid #e1e4e8', padding: '8px 12px', background: '#f6f8fa', textAlign: 'left', wordBreak: 'break-word' }}>{children}</th>,
            td: ({ children }) => <td style={{ border: '1px solid #e1e4e8', padding: '8px 12px', wordBreak: 'break-word' }}>{children}</td>,
            pre: ({ children }) => <Box component="pre" sx={{ ...WRAP_SX, whiteSpace: 'pre-wrap', maxWidth: '100%', overflowX: 'auto', my: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, fontSize: 13 }}>{children}</Box>,
        }}
    >{children || ''}</ReactMarkdown>
);

// ──────────────────────────────────────────────────────
// SSE stream chat — Phase 1.5 detached producer + Redis Stream
// ──────────────────────────────────────────────────────
// Pattern (giống Agent Hub Phase 1.5):
//   1. POST /chat/stream → backend mark active_turn, spawn producer, consumer
//      đọc Redis Stream. Event đầu tiên = `turn_open` với turn_id.
//   2. FE persist turn_id vào localStorage `kb:active:<collection>:<conv>`.
//      Nếu refresh page → mount → check `/collections/{id}/active-turn`.
//   3. GET /turns/{turn_id}/stream?from_id=<last_event_id> → resume từ chỗ
//      ngắt. Tokens không bị mất.
//
// Track from_id (Redis Stream entry ID) qua callback onProgress để FE có
// thể reconnect chính xác chỗ ngắt.
async function streamKnowledgeChat({
    collectionId, question, conversationId, fromId,
    turnId, // optional: nếu có → resume thay vì start mới
    onTurnOpen, onRetrieval, onToolUse, onToolResult, onText, onDone, onError, onCitations, onProgress,
}) {
    const baseUrl = (process.env.REACT_APP_API_URL || '/api/v1');
    let res;
    if (turnId) {
        // Resume mode
        const url = `${baseUrl}/knowledge/turns/${turnId}/stream?from_id=${encodeURIComponent(fromId || '0-0')}`;
        res = await fetch(url, { headers: { Authorization: getAuthHeader() } });
    } else {
        res = await fetch(`${baseUrl}/knowledge/collections/${collectionId}/chat/stream`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: getAuthHeader() },
            body: JSON.stringify({ question, conversation_id: conversationId || null })
        });
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    if (!res.body) throw new Error('No body');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        while (true) {
            const idx = buf.indexOf('\n\n');
            if (idx < 0) break;
            const block = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            let event = 'message';
            let data = null;
            let eid = null;
            for (const line of block.split('\n')) {
                if (line.startsWith('event:')) event = line.slice(6).trim();
                else if (line.startsWith('id:')) eid = line.slice(3).trim();
                else if (line.startsWith('data:')) {
                    try { data = JSON.parse(line.slice(5).trim()); } catch { data = null; }
                }
            }
            if (eid) onProgress?.(eid);
            if (data == null) continue;
            if (event === 'turn_open') onTurnOpen?.(data);
            else if (event === 'retrieval') onRetrieval?.(data);
            else if (event === 'tool_use') onToolUse?.(data);
            else if (event === 'tool_result') onToolResult?.(data);
            else if (event === 'text') onText?.(data.delta || '');
            else if (event === 'done') { onCitations?.(data.citations || []); onDone?.(data); }
            else if (event === 'error') onError?.(data);
        }
    }
}

// localStorage helpers — persist turn state qua refresh
const _activeTurnKey = (collectionId, conversationId) =>
    `kb:active:${collectionId}:${conversationId || 'default'}`;

const saveActiveTurn = (collectionId, conversationId, turnId, lastEventId) => {
    try {
        localStorage.setItem(_activeTurnKey(collectionId, conversationId),
            JSON.stringify({ turn_id: turnId, last_event_id: lastEventId || '0-0', ts: Date.now() }));
    } catch { /* quota or disabled */ }
};

const loadActiveTurn = (collectionId, conversationId) => {
    try {
        const raw = localStorage.getItem(_activeTurnKey(collectionId, conversationId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Stale > 1h → invalidate (TTL trùng backend)
        if (!parsed?.turn_id || Date.now() - (parsed.ts || 0) > 3600_000) return null;
        return parsed;
    } catch { return null; }
};

const clearActiveTurn = (collectionId, conversationId) => {
    try { localStorage.removeItem(_activeTurnKey(collectionId, conversationId)); } catch { /* ignore */ }
};

// ──────────────────────────────────────────────────────
// Components
// ──────────────────────────────────────────────────────
const NewCollectionDialog = ({ open, onClose, onCreated, projectId }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(null);
    const [options, setOptions] = useState({ embedding_models: [], llm_models: [] });
    const [embeddingModel, setEmbeddingModel] = useState('BAAI/bge-m3');
    const [llmModel, setLlmModel] = useState('claude-sonnet-4-6');

    // Fetch options khi dialog mở
    useEffect(() => {
        if (!open) return;
        let mounted = true;
        api.get('/knowledge/options')
            .then((res) => {
                if (!mounted) return;
                setOptions(res.data || { embedding_models: [], llm_models: [] });
                const defEmb = res.data?.embedding_models?.find((m) => m.default);
                const defLlm = res.data?.llm_models?.find((m) => m.default);
                if (defEmb) setEmbeddingModel(defEmb.id);
                if (defLlm) setLlmModel(defLlm.id);
            })
            .catch((e) => console.warn('Cannot load knowledge options', e));
        return () => { mounted = false; };
    }, [open]);

    const handleSubmit = async () => {
        if (!name.trim()) return;
        setSubmitting(true);
        try {
            const res = await api.post('/knowledge/collections', {
                name: name.trim(),
                description: description.trim() || null,
                project_id: projectId || null,
                embedding_model: embeddingModel,
                llm_model: llmModel
            });
            onCreated(res.data);
            setName('');
            setDescription('');
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || e?.message);
        } finally {
            setSubmitting(false);
        }
    };

    const embeddingMeta = options.embedding_models.find((m) => m.id === embeddingModel);
    const llmMeta = options.llm_models.find((m) => m.id === llmModel);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Tạo Knowledge Collection mới</DialogTitle>
            <DialogContent>
                <TextField autoFocus fullWidth label="Tên collection" placeholder="VD: Báo chí & sự tự tin lãnh đạo VN30"
                    value={name} onChange={(e) => setName(e.target.value)} sx={{ mt: 1 }} />
                <TextField fullWidth multiline rows={3} label="Mô tả (tuỳ chọn)"
                    value={description} onChange={(e) => setDescription(e.target.value)} sx={{ mt: 2 }} />

                <FormControl fullWidth sx={{ mt: 2 }} size="small">
                    <InputLabel id="lbl-emb">Embedding model</InputLabel>
                    <Select
                        labelId="lbl-emb"
                        value={embeddingModel}
                        label="Embedding model"
                        onChange={(e) => setEmbeddingModel(e.target.value)}
                    >
                        {options.embedding_models.map((m) => (
                            <MenuItem key={m.id} value={m.id}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{m.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {m.dim}d · {m.lang} · ~{m.size_gb}GB cache
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {embeddingMeta ? `${embeddingMeta.dim} dimensions · ${embeddingMeta.lang}` : ''}
                        {embeddingMeta?.default && ' · KHÔNG đổi sau khi tạo (gắn với vectors trong Qdrant)'}
                    </FormHelperText>
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }} size="small">
                    <InputLabel id="lbl-llm">LLM model (Q&A)</InputLabel>
                    <Select
                        labelId="lbl-llm"
                        value={llmModel}
                        label="LLM model (Q&A)"
                        onChange={(e) => setLlmModel(e.target.value)}
                    >
                        {options.llm_models.map((m) => (
                            <MenuItem key={m.id} value={m.id}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{m.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">cost: {m.cost}</Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        LLM dùng cho chat Q&A. Có thể đổi sau (chỉ ảnh hưởng chat tới, không re-embed).
                    </FormHelperText>
                </FormControl>

                {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Huỷ</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!name.trim() || submitting}>
                    {submitting ? <CircularProgress size={18} /> : 'Tạo'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const DocumentRow = ({ doc, onDelete }) => {
    const status = (doc.status || '').toLowerCase();
    const isProcessing = ['converting', 'chunking', 'embedding', 'pending'].includes(status);
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderBottom: '1px solid', borderColor: 'grey.100' }}>
            {fileIcon(doc.filename)}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.filename}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.3 }}>
                    <Chip size="small" label={STATUS_LABEL[status] || status}
                        color={STATUS_COLOR[status] || 'default'} sx={{ height: 18, fontSize: 10 }} />
                    {doc.chunk_count > 0 && <Typography variant="caption" color="text.secondary">{doc.chunk_count} chunks</Typography>}
                    <Typography variant="caption" color="text.secondary">{fmtSize(doc.size_bytes)}</Typography>
                </Box>
                {isProcessing && (
                    <LinearProgress variant="determinate" value={doc.progress || 0} sx={{ mt: 0.5, height: 3, borderRadius: 1 }} />
                )}
                {status === 'failed' && doc.error_message && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.3 }}>
                        {doc.error_message.slice(0, 200)}
                    </Typography>
                )}
            </Box>
            <Tooltip title="Xoá document">
                <IconButton size="small" onClick={() => onDelete(doc)} disabled={isProcessing}>
                    <IconTrash size={16} />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

const CitationCard = ({ c, idx }) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <Paper variant="outlined" sx={{ p: 1.25, mb: 1, borderRadius: 1.5, cursor: 'pointer' }}
            onClick={() => setExpanded(!expanded)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Chip size="small" label={`Nguồn ${idx}`} color="primary" sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
                <IconQuote size={13} />
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ...WRAP_SX }}>
                    {c.doc_name || `doc ${c.doc_id}`} · score {c.score?.toFixed?.(3) || c.score}
                    {c.rerank_score ? ` · rerank ${c.rerank_score}` : ''}
                </Typography>
                <IconChevronDown size={12} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.15s' }} />
            </Box>
            {c.heading_path && (
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 500, color: 'text.primary', mb: 0.5 }}>
                    {c.heading_path}
                </Typography>
            )}
            {expanded && c.text_preview && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5, whiteSpace: 'pre-wrap', ...WRAP_SX }}>
                    {c.text_preview}
                </Typography>
            )}
        </Paper>
    );
};

// P6: Export Q&A session
const exportSession = (messages, collectionName) => {
    const lines = [`# Knowledge Q&A — ${collectionName || 'Session'}`, `Exported: ${new Date().toISOString()}`, ''];
    for (const m of messages) {
        if (m.role === 'user') {
            lines.push(`## Q: ${m.content}`, '');
        } else {
            lines.push(m.content || '', '');
            if (m.citations?.length) {
                lines.push('### Sources', '');
                m.citations.forEach((c, i) => {
                    lines.push(`${i + 1}. ${c.doc_name || 'doc ' + c.doc_id} — ${c.heading_path || ''} (score ${c.score})`);
                });
                lines.push('');
            }
            lines.push('---', '');
        }
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `knowledge-qa-${(collectionName || 'session').replace(/\s+/g, '-')}.md`;
    a.click();
    URL.revokeObjectURL(url);
};

// ──────────────────────────────────────────────────────
// Main
// ──────────────────────────────────────────────────────
export default function KnowledgePage() {
    const [collections, setCollections] = useState([]);
    const [selected, setSelected] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [tab, setTab] = useState(0);  // 0=documents, 1=chat
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [newDialog, setNewDialog] = useState(false);
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const pollRef = useRef(null);

    // Chat state
    const [messages, setMessages] = useState([]);
    const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE);
    const lazyLoadRef = useRef(null);
    const isLoadingMoreRef = useRef(false);
    // Character-by-character animation: receive word chunks → animate per char
    const charQueueRef = useRef('');    // pending characters to animate
    const charTimerRef = useRef(null);  // setInterval handle
    const [chatInput, setChatInput] = useState('');
    const [qaSessions, setQaSessions] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [streaming, setStreaming] = useState(null);
    const [chatLoading, setChatLoading] = useState(false);
    const scrollRef = useRef(null);
    // Phase 1.5: turn tracking để resume sau refresh
    const turnIdRef = useRef(null);
    const lastEventIdRef = useRef('0-0');
    // Knowledge module hiện chưa multi-conversation per collection → conv_id = null;
    // sẵn shape khi backlog Phase 2 nâng cấp.
    const conversationIdRef = useRef(null);

    // Persist messages to localStorage for refresh recovery
    useEffect(() => {
        if (!selected) return;
        const key = `kb:msgs:${selected.id}:${conversationIdRef.current || 'default'}`;
        if (messages.length > 0) {
            try {
                localStorage.setItem(key, JSON.stringify({
                    messages: messages.slice(-20),
                    conversationId: conversationIdRef.current,
                    ts: Date.now(),
                }));
            } catch {}
        }
    }, [messages, selected]);

    // Restore on mount
    useEffect(() => {
        if (!selected) return;
        const key = `kb:msgs:${selected.id}:${conversationIdRef.current || 'default'}`;
        try {
            const saved = localStorage.getItem(key);
            if (saved && messages.length === 0) {
                const data = JSON.parse(saved);
                if (data.messages?.length && Date.now() - data.ts < 24 * 3600 * 1000) {
                    setMessages(data.messages);
                    if (data.conversationId) conversationIdRef.current = data.conversationId;
                }
            }
        } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    // Load collections
    const loadCollections = useCallback(async () => {
        try {
            const res = await api.get('/knowledge/collections');
            const list = res.data || [];
            setCollections(list);
            if (!selected && list.length > 0) setSelected(list[0]);
        } catch (e) {
            setError(e?.message || 'Cannot load');
        }
    }, [selected]);

    useEffect(() => { loadCollections(); }, []);

    // Load documents for selected collection + poll khi có processing
    const loadDocuments = useCallback(async (id) => {
        try {
            const res = await api.get(`/knowledge/collections/${id}/documents`);
            const list = res.data || [];
            setDocuments(list);
            // Tự poll nếu có document đang process
            const hasProcessing = list.some((d) => ['pending', 'converting', 'chunking', 'embedding'].includes((d.status || '').toLowerCase()));
            if (hasProcessing && !pollRef.current) {
                pollRef.current = setInterval(() => loadDocuments(id), 3000);
            } else if (!hasProcessing && pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        } catch (e) {
            setError(e?.message || 'Cannot load docs');
        }
    }, []);

    useEffect(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        if (selected) {
            loadDocuments(selected.id);
            setMessages([]);
            setStreaming(null);
            setVisibleCount(DEFAULT_VISIBLE);
        }
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [selected?.id, loadDocuments]);

    // Auto-scroll bottom on new message
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 300;
        if (nearBottom) el.scrollTop = el.scrollHeight;
    }, [messages, streaming]);

    // Lazy loading: IntersectionObserver on sentinel
    useEffect(() => {
        const sentinel = lazyLoadRef.current;
        const container = scrollRef.current;
        if (!sentinel || !container || messages.length <= visibleCount) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isLoadingMoreRef.current) {
                    isLoadingMoreRef.current = true;
                    const prev = container.scrollHeight;
                    setVisibleCount((c) => Math.min(c + VISIBLE_INCREMENT, messages.length));
                    requestAnimationFrame(() => {
                        container.scrollTop += (container.scrollHeight - prev);
                        isLoadingMoreRef.current = false;
                    });
                }
            },
            { root: container, rootMargin: '200px 0px 0px 0px', threshold: 0 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [messages.length, visibleCount]);

    // Auto-shrink when scroll to bottom
    const handleScrollShrink = useCallback(() => {
        const el = scrollRef.current;
        if (!el || visibleCount <= DEFAULT_VISIBLE) return;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
            setVisibleCount(DEFAULT_VISIBLE);
        }
    }, [visibleCount]);

    // Upload files
    const handleFiles = async (files) => {
        if (!selected || !files?.length) return;
        setUploading(true);
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append('file', file);
                await fetch(`${process.env.REACT_APP_API_URL || '/api/v1'}/knowledge/collections/${selected.id}/documents`, {
                    method: 'POST', headers: { Authorization: getAuthHeader() }, body: fd
                });
            }
            loadDocuments(selected.id);
        } catch (e) {
            setError(e?.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteDoc = async (doc) => {
        if (!window.confirm(`Xoá ${doc.filename}?`)) return;
        try {
            await api.delete(`/knowledge/collections/${selected.id}/documents/${doc.id}`);
            loadDocuments(selected.id);
        } catch (e) {
            setError(e?.message || 'Delete failed');
        }
    };

    const handleDeleteCollection = async () => {
        if (!selected) return;
        if (!window.confirm(`Xoá collection "${selected.name}"? Toàn bộ documents + vectors sẽ mất.`)) return;
        try {
            await api.delete(`/knowledge/collections/${selected.id}`);
            setSelected(null);
            loadCollections();
        } catch (e) {
            setError(e?.message || 'Delete failed');
        }
    };

    // Phase 1.5: resume turn từ chỗ ngắt (refresh hoặc reconnect)
    const resumeTurn = useCallback(async (turnId, fromId = '0-0', userQuestionEcho = null) => {
        if (!selected) return;
        if (chatLoading) return;
        // Nếu user vừa refresh — họ chưa thấy bubble câu hỏi. Echo lại từ meta server.
        if (userQuestionEcho) {
            setMessages((m) => [...m, { role: 'user', content: userQuestionEcho }]);
        }
        setChatLoading(true);
        setStreaming({ role: 'assistant', content: '', retrieval: null, citations: [], tool_trace: [] });
        turnIdRef.current = turnId;
        lastEventIdRef.current = fromId;

        try {
            await streamKnowledgeChat({
                collectionId: selected.id,
                turnId, fromId,
                onProgress: (eid) => {
                    lastEventIdRef.current = eid;
                    saveActiveTurn(selected.id, conversationIdRef.current, turnId, eid);
                },
                onRetrieval: (d) => setStreaming((cur) => ({ ...cur, retrieval: d })),
                onToolUse: (d) => setStreaming((cur) => ({
                    ...cur,
                    tool_trace: [...(cur?.tool_trace || []), { id: d.id, tool: d.tool, input: d.input, output_preview: '(pending)', success: false }],
                })),
                onToolResult: (d) => setStreaming((cur) => ({
                    ...cur,
                    tool_trace: (cur?.tool_trace || []).map((t) => t.id === d.id ? { ...t, output_preview: d.output_preview, success: d.success, latency_ms: d.latency_ms } : t),
                })),
                onText: (delta) => {
                    // Character-by-character: queue chars → animate 1 char per ~15ms
                    charQueueRef.current += delta;
                    if (!charTimerRef.current) {
                        charTimerRef.current = setInterval(() => {
                            const q = charQueueRef.current;
                            if (!q) { clearInterval(charTimerRef.current); charTimerRef.current = null; return; }
                            // Flush 3 chars per tick (45ms/3chars = smooth typing feel)
                            const batch = q.slice(0, 3);
                            charQueueRef.current = q.slice(3);
                            setStreaming((cur) => ({ ...cur, content: (cur?.content || '') + batch }));
                        }, 15);
                    }
                },
                onCitations: (cits) => setStreaming((cur) => ({ ...cur, citations: cits })),
                onDone: (d) => {
                    // Flush remaining chars + cleanup timer
                    if (charTimerRef.current) { clearInterval(charTimerRef.current); charTimerRef.current = null; }
                    const remaining = charQueueRef.current || '';
                    charQueueRef.current = '';
                    setStreaming((cur) => {
                        const final = { ...cur, content: (cur?.content || '') + remaining, usage: d?.usage };
                        setMessages((m) => [...m, final]);
                        return null;
                    });
                    clearActiveTurn(selected.id, conversationIdRef.current);
                    turnIdRef.current = null;
                    lastEventIdRef.current = '0-0';
                },
                onError: (d) => {
                    if (charTimerRef.current) { clearInterval(charTimerRef.current); charTimerRef.current = null; }
                    charQueueRef.current = '';
                    setError(d?.detail || 'Stream error'); setStreaming(null); clearActiveTurn(selected.id, conversationIdRef.current);
                }
            });
        } catch (e) {
            setError(e?.message || 'Resume failed');
            setStreaming(null);
        } finally {
            setChatLoading(false);
        }
    }, [selected, chatLoading]);

    // Auto-check active turn khi mở/chuyển collection → resume nếu có
    useEffect(() => {
        if (!selected) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get(`/knowledge/collections/${selected.id}/active-turn`,
                    { params: { conversation_id: conversationIdRef.current || undefined } });
                if (cancelled) return;
                if (res.data?.active) {
                    // Backend có turn đang chạy → ưu tiên server, dùng question từ meta
                    const local = loadActiveTurn(selected.id, conversationIdRef.current);
                    const fromId = (local?.turn_id === res.data.turn_id) ? (local.last_event_id || '0-0') : '0-0';
                    setTab(1); // switch sang tab Chat
                    resumeTurn(res.data.turn_id, fromId, res.data.question);
                } else {
                    // Server không có active turn → dọn localStorage
                    clearActiveTurn(selected.id, conversationIdRef.current);
                }
            } catch (e) {
                console.warn('active-turn check failed', e);
            }
        })();
        return () => { cancelled = true; };
    }, [selected?.id, resumeTurn]);

    const sendChat = async (text) => {
        if (!text.trim() || !selected || chatLoading) return;
        // Auto-generate conversation_id nếu chưa có (ensure session saved to DB)
        if (!conversationIdRef.current) {
            conversationIdRef.current = window.crypto?.randomUUID?.()
                || 'kqa-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
        }
        setMessages((m) => [...m, { role: 'user', content: text }]);
        setChatInput('');
        setChatLoading(true);
        setStreaming({ role: 'assistant', content: '', retrieval: null, citations: [], tool_trace: [] });
        lastEventIdRef.current = '0-0';

        try {
            await streamKnowledgeChat({
                collectionId: selected.id,
                question: text,
                conversationId: conversationIdRef.current,
                onTurnOpen: (d) => {
                    turnIdRef.current = d?.turn_id;
                    if (d?.turn_id) saveActiveTurn(selected.id, conversationIdRef.current, d.turn_id, '0-0');
                },
                onProgress: (eid) => {
                    lastEventIdRef.current = eid;
                    if (turnIdRef.current) {
                        saveActiveTurn(selected.id, conversationIdRef.current, turnIdRef.current, eid);
                    }
                },
                onRetrieval: (d) => setStreaming((cur) => ({ ...cur, retrieval: d })),
                onToolUse: (d) => setStreaming((cur) => ({
                    ...cur,
                    tool_trace: [...(cur?.tool_trace || []), { id: d.id, tool: d.tool, input: d.input, output_preview: '(pending)', success: false }],
                })),
                onToolResult: (d) => setStreaming((cur) => ({
                    ...cur,
                    tool_trace: (cur?.tool_trace || []).map((t) => t.id === d.id ? { ...t, output_preview: d.output_preview, success: d.success, latency_ms: d.latency_ms } : t),
                })),
                onText: (delta) => {
                    // Character-by-character: queue chars → animate 1 char per ~15ms
                    charQueueRef.current += delta;
                    if (!charTimerRef.current) {
                        charTimerRef.current = setInterval(() => {
                            const q = charQueueRef.current;
                            if (!q) { clearInterval(charTimerRef.current); charTimerRef.current = null; return; }
                            // Flush 3 chars per tick (45ms/3chars = smooth typing feel)
                            const batch = q.slice(0, 3);
                            charQueueRef.current = q.slice(3);
                            setStreaming((cur) => ({ ...cur, content: (cur?.content || '') + batch }));
                        }, 15);
                    }
                },
                onCitations: (cits) => setStreaming((cur) => ({ ...cur, citations: cits })),
                onDone: (d) => {
                    setStreaming((cur) => {
                        const final = { ...cur, usage: d?.usage };
                        setMessages((m) => [...m, final]);
                        return null;
                    });
                    clearActiveTurn(selected.id, conversationIdRef.current);
                    turnIdRef.current = null;
                    lastEventIdRef.current = '0-0';
                },
                onError: (d) => {
                    setError(d?.detail || 'Stream error');
                    setStreaming(null);
                    clearActiveTurn(selected.id, conversationIdRef.current);
                    turnIdRef.current = null;
                }
            });
        } catch (e) {
            setError(e?.message || 'Chat failed');
            setStreaming(null);
        } finally {
            setChatLoading(false);
        }
    };

    const readyDocCount = useMemo(() => documents.filter((d) => d.status === 'ready').length, [documents]);

    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 88px)', bgcolor: '#fff' }}>
            {/* Sidebar Collections */}
            {sidebarOpen && (
                <Paper elevation={0} sx={{ width: 300, borderRight: '1px solid', borderColor: 'grey.200', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: 16 }}>Knowledge</Typography>
                            <Typography variant="caption" color="text.secondary">{collections.length} collections</Typography>
                        </Box>
                        <Tooltip title="Tạo collection mới">
                            <IconButton size="small" color="primary" onClick={() => setNewDialog(true)}>
                                <IconPlus size={20} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {collections.length === 0 && (
                            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                                <IconBook2 size={36} style={{ opacity: 0.3 }} />
                                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>Chưa có collection. Tạo collection đầu tiên.</Typography>
                            </Box>
                        )}
                        <List dense disablePadding>
                            {collections.map((c) => (
                                <ListItemButton key={c.id} selected={selected?.id === c.id} onClick={() => setSelected(c)} sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                                    <ListItemText
                                        primary={<Typography variant="body2" sx={{ fontSize: 13.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</Typography>}
                                        secondary={
                                            <Box sx={{ display: 'flex', gap: 1, mt: 0.3 }}>
                                                <Typography variant="caption" color="text.secondary">{c.doc_count} files</Typography>
                                                <Typography variant="caption" color="text.secondary">·</Typography>
                                                <Typography variant="caption" color="text.secondary">{c.chunk_count} chunks</Typography>
                                            </Box>
                                        }
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Box>
                </Paper>
            )}

            {/* Main */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderBottom: '1px solid', borderColor: 'grey.200', minHeight: 56 }}>
                    <IconButton size="small" onClick={() => setSidebarOpen((s) => !s)}>
                        <IconLayoutSidebar size={20} />
                    </IconButton>
                    {selected ? (
                        <>
                            <IconBook2 size={20} color="#6366f1" />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{readyDocCount}/{documents.length} sẵn sàng · {selected.chunk_count} chunks · bge-m3</Typography>
                            </Box>
                            <Tooltip title="Refresh">
                                <IconButton size="small" onClick={() => { loadCollections(); loadDocuments(selected.id); }}>
                                    <IconRefresh size={18} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Xoá collection">
                                <IconButton size="small" color="error" onClick={handleDeleteCollection}>
                                    <IconTrash size={18} />
                                </IconButton>
                            </Tooltip>
                        </>
                    ) : (
                        <Typography color="text.secondary">Chọn collection để bắt đầu</Typography>
                    )}
                </Box>

                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>{error}</Alert>}

                {selected && (
                    <>
                        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid', borderColor: 'grey.200', px: 2 }}>
                            <Tab icon={<IconFile size={16} />} iconPosition="start" label={`Tài liệu (${documents.length})`} sx={{ minHeight: 42, fontSize: 13 }} />
                            <Tab icon={<IconMessageCircle size={16} />} iconPosition="start" label="Hỏi đáp" sx={{ minHeight: 42, fontSize: 13 }} />
                        </Tabs>

                        {/* Tab Documents */}
                        {tab === 0 && (
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                <Box sx={{ p: 2 }}>
                                    <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
                                        accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.md,.txt,.html,.png,.jpg,.jpeg,.webp"
                                        onChange={(e) => handleFiles(Array.from(e.target.files || []))} />
                                    <Button fullWidth variant="outlined" startIcon={<IconUpload size={18} />}
                                        onClick={() => fileInputRef.current?.click()} disabled={uploading}
                                        sx={{ py: 2, borderStyle: 'dashed', borderWidth: 2 }}>
                                        {uploading ? <><CircularProgress size={16} sx={{ mr: 1 }} />Đang upload...</> : 'Click hoặc kéo thả file (pdf/docx/xlsx/img/...)'}
                                    </Button>
                                </Box>
                                <Box sx={{ flex: 1, overflow: 'auto', borderTop: '1px solid', borderColor: 'grey.100' }}>
                                    {documents.length === 0 && (
                                        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                            <Typography variant="body2">Chưa có tài liệu nào. Upload file để bắt đầu.</Typography>
                                            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                                Hỗ trợ: PDF · DOCX · XLSX · CSV · Markdown · TXT · Image · HTML
                                            </Typography>
                                        </Box>
                                    )}
                                    {documents.map((d) => <DocumentRow key={d.id} doc={d} onDelete={handleDeleteDoc} />)}
                                </Box>
                            </Box>
                        )}

                        {/* Tab Chat */}
                        {tab === 1 && (
                            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                                {/* History sidebar */}
                                {showHistory && (
                                    <Box sx={{ width: 240, borderRight: '1px solid', borderColor: 'grey.200', overflow: 'auto', bgcolor: 'grey.50', flexShrink: 0 }}>
                                        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Lịch sử Q&A</Typography>
                                            <Button size="small" sx={{ minWidth: 0, fontSize: 11, textTransform: 'none' }}
                                                onClick={() => {
                                                    conversationIdRef.current = null;
                                                    setMessages([]);
                                                    setVisibleCount(DEFAULT_VISIBLE);
                                                }}>+ Mới</Button>
                                        </Box>
                                        <Divider />
                                        {qaSessions.map((s) => (
                                            <Box key={s.session_id}
                                                onClick={async () => {
                                                    try {
                                                        const r = await api.get(`/knowledge/collections/${selected.id}/sessions/${s.session_id}`);
                                                        const msgs = (r.data || []).map((m) => ({ role: m.role, content: m.content }));
                                                        setMessages(msgs);
                                                        conversationIdRef.current = s.session_id;
                                                        setVisibleCount(DEFAULT_VISIBLE);
                                                    } catch {}
                                                }}
                                                sx={{
                                                    px: 1.5, py: 1, cursor: 'pointer', borderBottom: '1px solid', borderColor: 'grey.100',
                                                    bgcolor: conversationIdRef.current === s.session_id ? 'primary.50' : 'transparent',
                                                    '&:hover': { bgcolor: 'grey.100' },
                                                }}>
                                                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', ...WRAP_SX }}>{s.first_question || '(empty)'}</Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{s.message_count} msgs</Typography>
                                            </Box>
                                        ))}
                                        {qaSessions.length === 0 && (
                                            <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>Chưa có lịch sử</Typography>
                                        )}
                                    </Box>
                                )}
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                {/* History toggle button */}
                                <Box sx={{ px: 2, py: 0.5, borderBottom: '1px solid', borderColor: 'grey.100', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title={showHistory ? 'Ẩn lịch sử' : 'Hiện lịch sử Q&A'}>
                                        <IconButton size="small" onClick={() => {
                                            setShowHistory(!showHistory);
                                            if (!showHistory && selected) {
                                                api.get(`/knowledge/collections/${selected.id}/sessions`).then((r) => setQaSessions(r.data || [])).catch(() => {});
                                            }
                                        }}><IconLayoutSidebar size={16} /></IconButton>
                                    </Tooltip>
                                    <Typography variant="caption" color="text.secondary">{messages.length} messages</Typography>
                                    <Box sx={{ flex: 1 }} />
                                    <Button size="small" sx={{ textTransform: 'none', fontSize: 11 }}
                                        onClick={() => { conversationIdRef.current = null; setMessages([]); setVisibleCount(DEFAULT_VISIBLE); }}>
                                        + Hội thoại mới
                                    </Button>
                                </Box>
                                <Box ref={scrollRef} onScroll={handleScrollShrink} sx={{ flex: 1, overflow: 'auto', p: 3, bgcolor: '#fff', minWidth: 0 }}>
                                    {messages.length === 0 && !streaming && (
                                        <Box sx={{ textAlign: 'center', mt: 6, color: 'text.secondary' }}>
                                            <IconMessageCircle size={40} style={{ opacity: 0.3 }} />
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                Hỏi đáp dựa trên {readyDocCount} tài liệu sẵn sàng trong "<strong>{selected.name}</strong>"
                                            </Typography>
                                            {readyDocCount === 0 && (
                                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                                    ⚠ Chưa có document nào ở trạng thái "Sẵn sàng" — upload + đợi pipeline xử lý xong
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                    <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto' }}>
                                        {/* Lazy loading sentinel */}
                                        {messages.length > visibleCount && (
                                            <Box ref={lazyLoadRef} sx={{ textAlign: 'center', py: 1, mb: 2 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    <CircularProgress size={12} sx={{ mr: 0.5, color: 'text.disabled' }} />
                                                    Scroll lên để xem {messages.length - visibleCount} tin nhắn cũ
                                                </Typography>
                                            </Box>
                                        )}
                                        {messages.slice(-visibleCount).map((m, i) => m.role === 'user' ? (
                                            <Box key={i} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                                                <Box sx={{ maxWidth: '85%', bgcolor: '#f0f4f8', borderRadius: 3, px: 2, py: 1.25, minWidth: 0 }}>
                                                    <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 14.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{m.content}</Typography>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Box key={i} sx={{ mb: 4 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                    <Box sx={{ width: 26, height: 26, borderRadius: 1, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <IconBook2 size={16} />
                                                    </Box>
                                                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Knowledge Q&A</Typography>
                                                </Box>
                                                <Box sx={{ pl: 4.5, minWidth: 0 }}>
                                                    {/* Grouped tool trace card */}
                                                    {m.tool_trace?.length > 0 && (
                                                        <Paper variant="outlined" sx={{ mb: 1.5, p: 1, borderRadius: 1.5, bgcolor: '#f8fdf8', borderColor: '#c8e6c9' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                                <IconCircleCheck size={12} color="#2e7d32" />
                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#2e7d32', fontSize: 11 }}>
                                                                    Pipeline ({m.tool_trace.length} steps)
                                                                </Typography>
                                                            </Box>
                                                            {m.tool_trace.map((t, ti) => (
                                                                <Box key={ti} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 2, py: 0.25 }}>
                                                                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: t.success ? '#4caf50' : '#bdbdbd' }} />
                                                                    <Typography variant="caption" sx={{ fontWeight: 500, fontSize: 11 }}>{t.tool}</Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{t.output_preview}</Typography>
                                                                    {t.latency_ms > 0 && <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>{t.latency_ms}ms</Typography>}
                                                                </Box>
                                                            ))}
                                                        </Paper>
                                                    )}
                                                    <Md>{m.content}</Md>
                                                    {m.citations?.length > 0 && (
                                                        <Accordion disableGutters elevation={0} sx={{ mt: 1, border: '1px solid', borderColor: 'grey.200', '&:before': { display: 'none' } }}>
                                                            <AccordionSummary expandIcon={<IconChevronDown size={16} />} sx={{ minHeight: 36 }}>
                                                                <Typography variant="caption" sx={{ fontWeight: 600 }}>{m.citations.length} citations</Typography>
                                                            </AccordionSummary>
                                                            <AccordionDetails>
                                                                {m.citations.map((c, idx) => <CitationCard key={idx} c={c} idx={idx} />)}
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    )}
                                                    {m.usage && (
                                                        <Box sx={{ mt: 1, display: 'flex', gap: 1.5, color: 'text.secondary', fontSize: 11.5 }}>
                                                            {m.usage.cost_usd > 0 && <span>${m.usage.cost_usd.toFixed(4)}</span>}
                                                            {m.usage.duration_ms > 0 && <span>· {(m.usage.duration_ms / 1000).toFixed(1)}s</span>}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        ))}
                                        {streaming && (
                                            <Box sx={{ mb: 4 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                    <Box sx={{ width: 26, height: 26, borderRadius: 1, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <IconBook2 size={16} />
                                                    </Box>
                                                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Knowledge Q&A</Typography>
                                                    {streaming.retrieval && (
                                                        <Chip size="small" label={`${streaming.retrieval.chunk_count} chunks retrieved`} sx={{ height: 18, fontSize: 10 }} />
                                                    )}
                                                </Box>
                                                <Box sx={{ pl: 4.5, minWidth: 0 }}>
                                                    {/* Live tool trace — grouped card */}
                                                    {streaming.tool_trace?.length > 0 && (
                                                        <Paper variant="outlined" sx={{ mb: 1.5, p: 1, borderRadius: 1.5, bgcolor: '#fffde7', borderColor: '#fff9c4' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                                <CircularProgress size={10} thickness={5} sx={{ color: '#f9a825' }} />
                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#f57f17', fontSize: 11 }}>
                                                                    Processing...
                                                                </Typography>
                                                            </Box>
                                                            {streaming.tool_trace.map((t, ti) => (
                                                                <Box key={ti} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 2, py: 0.25 }}>
                                                                    {t.output_preview === '(pending)'
                                                                        ? <CircularProgress size={8} thickness={5} sx={{ color: '#f9a825' }} />
                                                                        : <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: t.success ? '#4caf50' : '#f44336' }} />
                                                                    }
                                                                    <Typography variant="caption" sx={{ fontWeight: 500, fontSize: 11 }}>{t.tool}</Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, ...WRAP_SX }}>{t.output_preview}</Typography>
                                                                    {t.latency_ms > 0 && <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>{t.latency_ms}ms</Typography>}
                                                                </Box>
                                                            ))}
                                                        </Paper>
                                                    )}
                                                    {/* Raw text while streaming (skip expensive Markdown re-parse per token) */}
                                                    <Typography component="div" sx={{ whiteSpace: 'pre-wrap', fontSize: 14.5, lineHeight: 1.7, ...WRAP_SX }}>
                                                        {streaming.content || ''}
                                                    </Typography>
                                                    <Box sx={{ display: 'inline-block', width: 8, height: 14, bgcolor: 'primary.main', animation: 'blink 1s infinite' }} />
                                                    <style>{`@keyframes blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }`}</style>
                                                </Box>
                                            </Box>
                                        )}
                                        {chatLoading && !streaming && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 4.5, color: 'text.secondary' }}>
                                                <CircularProgress size={14} />
                                                <Typography variant="caption">Đang tìm chunks liên quan...</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                                {/* P5: Follow-up suggestions + P6: Export */}
                                {messages.length > 0 && !chatLoading && (
                                    <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'grey.100', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="caption" color="text.secondary">Gợi ý:</Typography>
                                        {(messages[messages.length - 1]?.citations || []).slice(0, 3).map((c, i) => (
                                            c.heading_path ? (
                                                <Chip key={i} size="small" variant="outlined" label={c.heading_path.slice(0, 40)}
                                                    onClick={() => sendChat(`Nội dung chi tiết về: ${c.heading_path}`)}
                                                    sx={{ height: 22, fontSize: 11, cursor: 'pointer' }} />
                                            ) : null
                                        ))}
                                        <Box sx={{ flex: 1 }} />
                                        <Tooltip title="Export Q&A session → Markdown file">
                                            <Button size="small" variant="text" sx={{ textTransform: 'none', fontSize: 11, minWidth: 0 }}
                                                onClick={() => exportSession(messages, selected?.name)}>
                                                Export .md
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                )}
                                <Box sx={{ borderTop: '1px solid', borderColor: 'grey.200', p: 2 }}>
                                    <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto', display: 'flex', alignItems: 'flex-end', gap: 1,
                                              border: '1px solid', borderColor: 'grey.300', borderRadius: 3, px: 1.5, py: 1,
                                              '&:focus-within': { borderColor: 'primary.main' } }}>
                                        <TextField fullWidth multiline maxRows={6}
                                            placeholder={`Hỏi về tài liệu trong "${selected.name}"...`}
                                            value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(chatInput); } }}
                                            disabled={chatLoading || readyDocCount === 0}
                                            variant="standard"
                                            InputProps={{ disableUnderline: true, sx: { fontSize: 14.5, lineHeight: 1.5 } }} />
                                        <IconButton onClick={() => sendChat(chatInput)} disabled={chatLoading || !chatInput.trim() || readyDocCount === 0}
                                            sx={{ bgcolor: chatInput.trim() ? 'primary.main' : 'grey.300', color: 'white', width: 32, height: 32, '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}>
                                            {chatLoading ? <CircularProgress size={14} color="inherit" /> : <IconSend size={16} />}
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Box>
                            </Box>
                        )}
                    </>
                )}
            </Box>

            <NewCollectionDialog open={newDialog} onClose={() => setNewDialog(false)} onCreated={(c) => { setCollections((cs) => [c, ...cs]); setSelected(c); }} />
        </Box>
    );
}
